import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const ChatBox = ({ streamId }) => {
  const userId = localStorage.getItem('user_id');
  const user = userId ? { id: userId, first_name: 'User', last_name: '' } : null; 
  // Ideally fetch full user info, but for chat we can just use ID if we don't have first_name locally.
  // Actually, we can fetch the user details if needed, or pass it in.
  
  const [activeTab, setActiveTab] = useState('chat'); // chat, qa, polls
  const [messages, setMessages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [polls, setPolls] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetch(`/api/chat/${streamId}`)
      .then(res => res.json())
      .then(data => { if (data.success) setMessages(data.messages); })
      .catch(err => console.error(err));

    socketRef.current = io();

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      socketRef.current.emit('join_stream', streamId);
    });

    socketRef.current.on('new_message', (message) => {
      setMessages(prev => [...prev, message]);
    });

    socketRef.current.on('new_question', (q) => {
      setQuestions(prev => [...prev, q]);
    });

    socketRef.current.on('question_upvoted', ({ questionId }) => {
      setQuestions(prev => prev.map(q => q.id === questionId ? { ...q, upvotes: q.upvotes + 1 } : q));
    });

    socketRef.current.on('new_poll', (p) => {
      setPolls(prev => [...prev, p]);
    });

    socketRef.current.on('poll_voted', ({ pollId, optionIndex }) => {
      setPolls(prev => prev.map(p => {
        if (p.id === pollId) {
          const newOptions = [...p.options];
          newOptions[optionIndex].votes += 1;
          return { ...p, options: newOptions };
        }
        return p;
      }));
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
    });

    return () => socketRef.current.disconnect();
  }, [streamId]);

  useEffect(() => {
    if (activeTab === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;
    socketRef.current.emit('send_message', {
      streamId,
      userId: user.id,
      userName: 'User ' + user.id,
      message: newMessage.trim(),
    });
    setNewMessage('');
  };

  const handleSendQuestion = (e) => {
    e.preventDefault();
    if (!newQuestion.trim() || !user) return;
    socketRef.current.emit('submit_question', {
      streamId,
      user: 'User ' + user.id,
      question: newQuestion.trim(),
    });
    setNewQuestion('');
  };

  const handleUpvote = (questionId) => {
    if (!user) return;
    socketRef.current.emit('upvote_question', { streamId, questionId });
  };

  const handleVotePoll = (pollId, optionIndex) => {
    if (!user) return;
    socketRef.current.emit('vote_poll', { streamId, pollId, optionIndex });
  };

  return (
    <div className="chat-box d-flex flex-column h-100" style={{ maxHeight: '400px', backgroundColor: 'var(--card-bg)', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--border-color)' }}>
      
      {/* Tabs */}
      <div className="d-flex border-bottom bg-dark rounded-top">
        <button className={`btn flex-fill text-white rounded-0 ${activeTab === 'chat' ? 'bg-primary' : ''}`} onClick={() => setActiveTab('chat')}>Chat</button>
        <button className={`btn flex-fill text-white rounded-0 ${activeTab === 'qa' ? 'bg-primary' : ''}`} onClick={() => setActiveTab('qa')}>Q&A</button>
        <button className={`btn flex-fill text-white rounded-0 ${activeTab === 'polls' ? 'bg-primary' : ''}`} onClick={() => setActiveTab('polls')}>Polls</button>
      </div>
      
      {activeTab === 'chat' && (
        <>
          <div className="chat-messages flex-grow-1 p-3 overflow-auto">
            {messages.length === 0 ? <p className="text-muted text-center mt-4">No messages yet.</p> : messages.map((msg, index) => (
              <div key={index} className={`mb-3 ${msg.user_id == user?.id ? 'text-end' : ''}`}>
                <div className="small text-muted mb-1">{msg.user_name}</div>
                <div className={`d-inline-block p-2 rounded`} style={{ backgroundColor: msg.user_id == user?.id ? 'var(--primary-color)' : 'rgba(255,255,255,0.1)', color: 'white', maxWidth: '85%', wordBreak: 'break-word' }}>
                  {msg.message}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input p-3 border-top">
            {user ? (
              <form onSubmit={handleSendMessage} className="d-flex gap-2">
                <input type="text" className="form-control bg-dark text-white border-secondary" placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} disabled={!isConnected} />
                <button type="submit" className="btn btn-primary" disabled={!isConnected || !newMessage.trim()}>Send</button>
              </form>
            ) : <div className="text-center text-muted small">Please <a href="/login" className="text-primary">log in</a> to chat.</div>}
          </div>
        </>
      )}

      {activeTab === 'qa' && (
        <>
          <div className="chat-messages flex-grow-1 p-3 overflow-auto">
            {questions.length === 0 ? <p className="text-muted text-center mt-4">No questions yet. Ask something!</p> : questions.sort((a,b)=>b.upvotes - a.upvotes).map((q, index) => (
              <div key={index} className="mb-3 bg-dark p-2 rounded border border-secondary d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted mb-1">{q.user}</div>
                  <div className="text-white">{q.question}</div>
                </div>
                <button className="btn btn-sm btn-outline-warning" onClick={() => handleUpvote(q.id)}>👍 {q.upvotes}</button>
              </div>
            ))}
          </div>
          <div className="chat-input p-3 border-top">
            {user ? (
              <form onSubmit={handleSendQuestion} className="d-flex gap-2">
                <input type="text" className="form-control bg-dark text-white border-secondary" placeholder="Ask a question..." value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} disabled={!isConnected} />
                <button type="submit" className="btn btn-warning text-dark" disabled={!isConnected || !newQuestion.trim()}>Ask</button>
              </form>
            ) : <div className="text-center text-muted small">Please log in to ask questions.</div>}
          </div>
        </>
      )}

      {activeTab === 'polls' && (
        <div className="chat-messages flex-grow-1 p-3 overflow-auto">
          {polls.length === 0 ? <p className="text-muted text-center mt-4">No active polls.</p> : polls.map((p, index) => (
            <div key={index} className="mb-4 bg-dark p-3 rounded border border-info">
              <h6 className="text-info mb-3">{p.question}</h6>
              {p.options.map((opt, oIdx) => {
                const totalVotes = p.options.reduce((sum, o) => sum + o.votes, 0);
                const percent = totalVotes === 0 ? 0 : Math.round((opt.votes / totalVotes) * 100);
                return (
                  <div key={oIdx} className="mb-2">
                    <button className="btn btn-outline-info w-100 text-start d-flex justify-content-between align-items-center" onClick={() => handleVotePoll(p.id, oIdx)}>
                      <span>{opt.text}</span>
                      <span>{percent}%</span>
                    </button>
                    <div className="progress mt-1" style={{ height: '5px' }}>
                      <div className="progress-bar bg-info" role="progressbar" style={{ width: `${percent}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatBox;
